import { Chart } from "angular-highcharts";
import { CreatePulseDialogComponent } from "../../../../../pages/kepler/pulse/create-pulse-dialog/create-pulse-dialog.component";
import { Formatter } from "../../../../../shared/pipes/app-format.pipe";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

export function getDisplayLables(this: any, commonService: any) {
  this.displayLabels = commonService.getLabels();
}

export function getGMapRandomColor() {
  const colorNum = 360;
  const color = Math.floor(Math.random() * colorNum);
  return `hsla(${color}, 50%, 25%, 90%)`;
}

export function getPerformanceImpact(pulse: any) {
  let perfImpact = "";
  pulse.performance_impact.forEach((impact: any) => {
    perfImpact += impact.display_name + ", ";
  });
  perfImpact = perfImpact.trimEnd();
  if (perfImpact.endsWith(",")) {
    perfImpact = perfImpact.slice(0, -1);
  }
  return perfImpact;
}

export function intializeData(this: any) {
  this.pulsesList.forEach((pulse: any) => {
    pulse["color"] = getGMapRandomColor();
    pulse["impact"] = getPerformanceImpact(pulse);
  });
}

export function getPulseCounts(
  this: any,
  portfolioId: any = null,
  subportfolioId: any = null,
  programId: any = null,
  projectId: any = null,
  vendorId: any = null
) {
  let qParam = "";
  if (projectId) {
    qParam += `&project_id=${projectId}`;
  } else if (programId) {
    qParam += `&program_id=${programId}`;
  } else if (subportfolioId) {
    qParam += `&subportfolio_id=${subportfolioId}`;
  } else if (portfolioId) {
    qParam += `&portfolio_id=${portfolioId}`;
  }
  if (vendorId) {
    qParam += `&vendor_id=${vendorId}`;
  }
  this.projectService
    .getPulseCounts(qParam)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((resp: any) => {
      this.pulseCounts = resp;
    });
}

export function initializeMetrics(this: any): void {
  this.metricValue = [];
  this.percentValue = [];
  this.month = [];
}

export function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getActualValue(val: any) {
  const lastValue = val?.actual_value[val?.actual_value.length - 1];
  if (
    [
      "attrition_currentmonth",
      "krr_tt",
      "attrition_ltm",
      "attrition_ytd",
    ].includes(val.name)
  ) {
    return roundToTwoDecimals(lastValue?.percentage || lastValue?.actual_value);
  } else if (val.name === "experience_profile") {
    return roundToTwoDecimals(lastValue?.data?.ratio);
  } else if (["gdp_survey_rating", "bvt_survey_rating"].includes(val.name)) {
    return roundToTwoDecimals(lastValue?.actual_value);
  } else {
    return roundToTwoDecimals(lastValue?.actual_value);
  }
}

export function processActualValues(
  this: any,
  actualValues: any[]
): {
  gdpName: any[];
  gdpValue: any[];
} {
  const gdpName: any[] = [];
  const gdpValue: any[] = [];

  actualValues.forEach((matric: any) => {
    this.metricValue.push(roundToTwoDecimals(matric.actual_value));
    this.month.push(matric.month_short);

    if (matric.percentage) {
      this.percentValue.push(roundToTwoDecimals(matric.percentage));
    }

    if (matric.gdp_name !== "ALL") {
      gdpValue.push(roundToTwoDecimals(matric.actual_value));
      gdpName.push(matric.gdp_name);
    }
  });

  return { gdpName, gdpValue };
}

export function formatNumber(number: any, decimalPlaces: number): string {
  // Ensure the number is a valid number
  if (!isNaN(number)) {
    // Use toFixed to format the number with the specified decimal places
    return number.toFixed(decimalPlaces);
  } else {
    // Handle the case where the input is not a valid number
    return number;
  }
}

export function constructDataObject(
  val: any,
  actualValue: any,
  change: any,
  lineTrendGrph: any,
  lineTrendOverview: any
): any {
  let variance = 0;
  if (val?.variance_percent) {
    variance = roundToTwoDecimals(val?.variance_percent);
  }
  const seriesOpt =
    lineTrendGrph?.options?.series?.map((s: any) => ({
      ...s,
      data:
        Array.isArray(s.data) && s.data.length > 0 ? s.data.slice(-3) : s.data,
    })) || [];

  // Also slice the x-axis categories to match the last 3 data points
  const xAxisOpt = lineTrendGrph?.options?.xAxis;
  let slicedXAxis = xAxisOpt;

  if (xAxisOpt) {
    if (Array.isArray(xAxisOpt)) {
      slicedXAxis = xAxisOpt.map((axis: any) => ({
        ...axis,
        categories:
          Array.isArray(axis.categories) && axis.categories.length > 0
            ? axis.categories.slice(-3)
            : axis.categories,
      }));
    } else if (xAxisOpt.categories && Array.isArray(xAxisOpt.categories)) {
      slicedXAxis = {
        ...xAxisOpt,
        categories: xAxisOpt.categories.slice(-3),
      };
    }
  }

  const balanceTrendChart = new Chart({
    ...(lineTrendGrph?.options || {}),
    series: seriesOpt,
    xAxis: slicedXAxis,
  } as any);

  return {
    change,
    trend: lineTrendGrph,
    balanceTrend: ["open_req", "experience_profile"].includes(val.name)
      ? lineTrendGrph
      : balanceTrendChart,
    overviewTrend: lineTrendOverview,
    id: val.id,
    metric_name: val.name,
    name: val.display_name,
    desc: val.description,
    actual_value: formatNumber(actualValue, 2),
    variance_per: variance,
    is_update: val.is_uptrend,
    status: val.status,
    zohoreport: val?.zohoreport,
    unit_name: val?.unit_name,
    // Set once an action has been raised for this metric; drives the Action column.
    action_id: val?.action_id ?? null,
    value: val,
  };
}

export function getChange(val: any): any {
  if (
    [
      "experience_profile",
      "req_aging",
      "gdp_survey_rating",
      "bvt_survey_rating",
    ].includes(val.name)
  ) {
    return "false";
  } else {
    return val.variance;
  }
}

export function reqAgingGraph(
  width: any,
  height: any,
  row: any,
  column: any,
  tooltipName: any
) {
  return new Chart({
    title: {
      text: "",
    },
    chart: {
      type: "column",
      width,
      height,
    },
    credits: false,
    yAxis: {
      min: 0,
      title: "",
    },
    xAxis: {
      categories: row,
      crosshair: true,
    },
    noData: {
      style: {
        fontWeight: "bold",
        fontSize: "12px",
        color: "#606060",
      },
      position: {
        align: "left",
        verticalAlign: "middle",
      },
    },

    tooltip: {
      // valueSuffix: ' (1000 MT)'
    },
    plotOptions: {
      column: {
        // pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: tooltipName,
        data: column,
      },
    ],
    exporting: { enabled: false },
  } as any);
}

export function returnSparkLineChart(
  seriesData: any,
  type: any,
  categories: any,
  forPdf = false
) {
  let plotSeriesOpt: any = {
    marker: {
      enabled: false,
    },
  };
  if (forPdf) {
    plotSeriesOpt = {
      marker: {
        enabled: true,
        radius: 2,
      },
      dataLabels: {
        enabled: true,
        formatter(this: any): string {
          if (this.y >= 1e9) {
            return (this.y / 1e9).toFixed(1) + "B";
          } else if (this.y >= 1e6) {
            return (this.y / 1e6).toFixed(1) + "M";
          } else if (this.y >= 1e3) {
            return (this.y / 1e3).toFixed(1) + "K";
          } else {
            return this.y.toString();
          }
        },
        style: {
          fontSize: "5px",
        },
      },
    };
  }
  return new Chart({
    title: {
      text: "",
    },
    credits: false,
    chart: {
      type,
      width: 85,
      height: 30,
      margin: [0, 0, 8, 0],
      style: {
        overflow: "visible",
      },
      skipClone: true,
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tickPositions: [0],
    },
    xAxis: {
      categories,
      lineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: "transparent",
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
    },
    legend: {
      enabled: false,
    },
    noData: {
      style: {
        fontWeight: "bold",
        fontSize: "12px",
        color: "#606060",
      },
      position: {
        align: "left",
        verticalAlign: "middle",
      },
    },
    plotOptions: {
      series: plotSeriesOpt,
    },
    tooltip: {
      enabled: true,
      outside: true,
      formatter(this: any): string {
        return `<b>${this.x}</b></br>${this.y.toLocaleString()}`;
      },
    },
    series: seriesData,
    exporting: { enabled: false },
  } as any);
}

export function returnSparkLineChartOverView(
  seriesData: any,
  type: any,
  categories: any
) {
  const marginLeft = 6;
  return new Chart({
    title: {
      text: "",
    },
    credits: false,
    chart: {
      type,
      margin: [2, 0, marginLeft, 0],
      backgroundColor: "#f9f9f9",
      renderTo: "container",
      height: 60,
      style: {
        overflow: "visible",
      },
      skipClone: true,
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tickPositions: [0],
    },
    xAxis: {
      categories,
      lineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: "transparent",
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
        },
        dataLabels: {
          shape: "connector",
          enabled: true,
          formatter(this: any): string {
            return `<span style="font-size:8px;font-weight:100">${this.x}</span>`;
          },
          y: -60,
          allowOverlap: true,
          useHTML: false,
        },
      },
    },
    tooltip: {
      enabled: true,
      outside: true,
      formatter(this: any): string {
        return `<b>${this.x}</b></br>${this.y.toLocaleString()}`;
      },
    },
    noData: {
      style: {
        fontWeight: "bold",
        fontSize: "12px",
        color: "#606060",
      },
      position: {
        align: "left",
        verticalAlign: "middle",
      },
    },
    series: seriesData,
    exporting: { enabled: false },
  } as any);
}

export function reqAgingGraphOverview(
  row: any,
  column: any,
  displayName: any,
  xAxisName: any,
  yAxisName: any
) {
  return new Chart({
    title: {
      text: "",
    },
    chart: {
      type: "column",
      height: "300",
    },
    credits: false,
    yAxis: {
      min: 0,
      title: {
        text: yAxisName,
      },
    },
    xAxis: {
      categories: row,
      crosshair: true,
      title: {
        text: xAxisName,
      },
    },

    tooltip: {
      // valueSuffix: ' (1000 MT)'
    },
    plotOptions: {
      column: {
        // pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    legend: {
      enabled: true,
    },
    series: [
      {
        name: displayName,
        data: column,
      },
    ],
    noData: {
      style: {
        fontWeight: "bold",
        fontSize: "12px",
        color: "#606060",
      },
      position: {
        align: "left",
        verticalAlign: "middle",
      },
    },
    exporting: { enabled: false },
  } as any);
}

export function pushDataToRelevantType(
  this: any,
  type: string,
  data: any
): void {
  switch (type) {
    case "engg":
      this.engineerData.push(data);
      break;
    case "people":
      this.peopleData.push(data);
      break;
    case "finance":
      this.financeData.push(data);
      break;
    case "customer":
      this.customerData.push(data);
      break;
    default:
      break;
  }
}

export function reloadMetricDataOnOverview(
  this: any,
  quadrantId: any,
  quadrantMetric: any
) {
  if (this.setMetricOverviewDetails?.quadrant_id && this.matrixOverView) {
    let metric;
    if (quadrantId === this.setMetricOverviewDetails?.quadrant_id) {
      metric = quadrantMetric.find(
        (x: any) => x.id === this.setMetricOverviewDetails?.metric_id
      );
      if ((metric === undefined || metric === null) && this.cardMatric) {
        metric = this.cardMatric;
        metric.actual_value = null;
        metric.change = null;
        metric.overviewTrend = {};
        metric.trend = {};
        metric.value.actual_value = [];
        metric.status = null;
        metric.variance_per = null;
      }
      const data: any = {
        metric,
        cardDetails: quadrantMetric,
        cardName: this.setMetricOverviewDetails?.quadrant_name,
      };
      this.scorecardService.changeMessage(data);
    }
  }
}

export function saveSortedMatricsData(this: any, data: any, quadrantId: any) {
  this.spinner.show();
  const matrics: any = [];
  data.forEach((mat: any) => {
    matrics.push(mat.id);
  });
  const payload = { quadrant_id: quadrantId, sort_order: matrics };
  this.projectService.saveSortedQuadrant(payload).subscribe(
    () => {
      this.spinner.hide();
    },
    () => {
      this.spinner.hide();
    }
  );
}

export async function loadLinkedMatricsData(this: any, metricId: any) {
  this.linkedMatrics = [];
  this.projectService
    .getLinkedMatricsData(
      metricId,
      this.projectId,
      this.portfolioId,
      this.subportfolioId,
      this.programId,
      this.vendorId
    )
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((response: any) => {
      let matricValue: any = [];
      let month: any = [];
      response.forEach((val: any) => {
        matricValue = [];
        month = [];
        val.actual_value.forEach((matric: any) => {
          matricValue.push(roundToTwoDecimals(matric.actual_value));
          month.push(matric.month_short);
        });
        val.trend = returnSparkLineChart(
          [{ data: matricValue }],
          "line",
          month
        );
        const actualValueLen = val?.actual_value?.length || 0;
        let last = val.actual_value[actualValueLen - 1];
        let secondLast = val.actual_value[actualValueLen - 2];
        val.change = last?.actual_value - secondLast?.actual_value;
        if (val.change) {
          val.change = roundToTwoDecimals(val.change);
        } else {
          val.change = 0;
        }
        let denominator = 1;
        if (secondLast?.actual_value) {
          denominator = secondLast.actual_value;
        }
        val.percent_change = roundToTwoDecimals(
          (val.change / denominator) * 100
        );
        this.linkedMatrics.push(val);
      });
    });
}

export async function loadPerformanceBenchMarcData(this: any, metricId: any) {
  console.log(metricId);
  this.projectService
    .getPerformanceBenchMarkData(
      metricId,
      this.projectId,
      this.portfolioId,
      this.subportfolioId,
      this.programId,
      this.vendorId
    )
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((response: any) => {
      this.performanceBenchMark = [];
      let metricValue: any = [];
      let month: any = [];
      response.forEach((val: any) => {
        metricValue = [];
        month = [];
        val.actual_value.forEach((matric: any) => {
          metricValue.push(matric.actual_value);
          month.push(matric.month_short);
        });
        val.trend = returnSparkLineChart(
          [{ data: metricValue }],
          "line",
          month
        );
        const actualValueLen = val?.actual_value?.length || 0;
        let last = val.actual_value[actualValueLen - 1];
        let secondLast = val.actual_value[actualValueLen - 2];
        val.change = last?.actual_value - secondLast?.actual_value;
        if (val.change) {
          val.change = roundToTwoDecimals(val.change);
        } else {
          val.change = 0;
        }
        let denominator = 1;
        if (secondLast?.actual_value) {
          denominator = secondLast.actual_value;
        }
        val.percent_change = roundToTwoDecimals(
          (val.change / denominator) * 100
        );
        this.performanceBenchMark.push(val);
      });
    });
}

export function getPulsesInfo(this: any) {
  this.pulsesInfo = [];
  this.pulseInfoLoaded = false;
  this.sortOptions = JSON.parse(JSON.stringify(this.sortOpt));
  if (this.sortOptions.indexOf(this.sortRecords) === -1) {
    this.sortRecords = this.endValue - this.startValue;
  }
  this.projectService
    .getPulses(
      this.projectId,
      this.portfolioId,
      this.subportfolioId,
      this.programId,
      this.vendorId,
      this.startValue,
      this.endValue
    )
    .subscribe((res: any) => {
      this.pulsesInfo = res;
      this.pulseInfoLoaded = true;
      if (this.sortRecords > this.pulsesInfo.count) {
        this.sortRecords = this.pulsesInfo.count;
        this.sortOptions.unshift(this.sortRecords);
      }
      if (this.sortOptions.indexOf(this.sortRecords) === -1) {
        this.sortOptions.unshift(this.sortRecords);
      }
    });
}

function getPulseFilterQueryParams(this: any) {
  let qprm = "";
  if (this.projectId) {
    qprm += `project=${this.projectId}`;
  }
  if (this.vendorId) {
    qprm += "&vendor=" + this.vendorId;
  } else if (this.programId) {
    qprm += "&program=" + this.programId;
  } else if (this.subportfolioId) {
    qprm += "&sub_portfolio=" + this.subportfolioId;
  } else if (this.portfolioId) {
    qprm += "&portfolio=" + this.portfolioId;
  } else {
    this.loggingService.warn(this.warningText);
  }
  return qprm;
}

export function getPulseTiles(this: any) {
  this.pulsesInfo = [];
  this.pulseInfoLoaded = false;
  let qprm = getPulseFilterQueryParams.bind(this)();
  this.surveyService.getFilteredPulses(qprm).subscribe((res: any) => {
    this.pulsesInfo = res;
    this.pulsesInfo.forEach((pulse: any) => {
      pulse["color"] = getGMapRandomColor();
      pulse["impact"] = getPerformanceImpact(pulse);
    });
    this.pulseInfoLoaded = true;
  });
}

export function getScoreCardQuadrantData(this: any) {
  this.spinner.show();
  if (this.scoreCardQuadrantSubscription$) {
    this.scoreCardQuadrantSubscription$.next();
    this.scoreCardQuadrantSubscription$.complete();
  }
  this.scoreCardQuadrantSubscription$ = new Subject<void>();
  this.dataLoaded = false;
  this.scorecardLoading.emit(false);
  this.projectService
    .getscoreCardQuadrant(
      this.projectId,
      this.portfolioId,
      this.subportfolioId,
      this.programId,
      this.vendorId
    )
    .pipe(takeUntil(this.scoreCardQuadrantSubscription$))
    .subscribe(
      (response: any) => {
        this.customerData = [];
        this.financeData = [];
        this.engineerData = [];
        this.peopleData = [];
        // Clear what's rendered too; a quadrant missing from this response would
        // otherwise keep showing the previous filter's rows.
        this.refreshQuadrantDisplays();
        let downTrendMatrics: any = [];
        this.spinner.hide();
        this.scorecardService.okrSpread(response?.objectives);
        this.scorecardService.riskSpread(response?.risk);
        if (response?.metrics?.length > 0) {
          response?.metrics.forEach((res: any) => {
            res.metrics
              .filter((metric: any) => metric?.status === "red")
              .forEach((metric: any) => {
                downTrendMatrics.push({
                  name: metric?.display_name,
                  description: metric?.description,
                });
              });
            if (res.id === this.enggId) {
              this.quadrants.engineering = res.display_name;
              this.dataToDelivery("engg", res.metrics);
              reloadMetricDataOnOverview.bind(this)(res.id, this.engineerData);
              this.noDataDelivery = this.engineerData.length === 0;
            }
            if (res.id === this.peopleId) {
              this.quadrants.people = res.display_name;
              this.dataToDelivery("people", res.metrics);
              reloadMetricDataOnOverview.bind(this)(res.id, this.peopleData);
              this.noDataPeople = this.peopleData.length === 0;
            }
            if (res.id === this.financeId) {
              this.quadrants.finance = res.display_name;
              this.dataToDelivery("finance", res.metrics);
              reloadMetricDataOnOverview.bind(this)(res.id, this.financeData);
              this.noDataFinance = this.financeData.length === 0;
            }
            if (res.id === this.customerId) {
              this.quadrants.customer = res.display_name;
              this.dataToDelivery("customer", res.metrics);
              reloadMetricDataOnOverview.bind(this)(res.id, this.customerData);
              this.noDataCustomer = this.customerData.length === 0;
            }
          });
          this.messageService.setGnews({
            pagename: "Balanced Scorecard",
            queryParam: { metric_list: JSON.stringify(downTrendMatrics) },
          });
        } else {
          this.noDataCustomer = true;
          this.noDataFinance = true;
          this.noDataDelivery = true;
          this.noDataPeople = true;
        }
        this.dataLoaded = true;
        this.scorecardLoading.emit(true);
      },
      (error: any) => {
        this.noDataCustomer = true;
        this.noDataFinance = true;
        this.noDataPeople = true;
        this.noDataDelivery = true;
        this.dataLoaded = true;
        this.loggingService.error(error);
      }
    );
}

export function handleDestroyal(this: any) {
  this.programId = null;
  this.portfolioId = null;
  this.subportfolioId = null;
  this.programId = null;
  this.scorecardService.changeMessage("nodata");
  this.scorecardService.okrSpread("");
  this.scorecardService.riskSpread("");
  this.filterService.filterCfgs.next({});
  this.unsubscribe$.next();
  this.unsubscribe$.complete();
  this.filterService.setFilterValue("");
}

export function handleEditPulse(this: any, pulse: any) {
  const dialog = this.dialog.open(CreatePulseDialogComponent, {
    width: "50vw",
    maxHeight: "80vh",
    minHeight: "63vh",
    disableClose: true,
    autoFocus: false,
    data: {
      pulseId: pulse.id,
    },
  });
  dialog.afterClosed().subscribe((data: any) => {
    if (data) {
      setTimeout(() => {
        getPulsesInfo.bind(this)();
      }, 1000);
    }
  });
}

export function getFormattedVal(val: any) {
  let formattedVal: any = 0;
  if (val) {
    const absVal = Math.abs(val);
    if (absVal >= 1e9) {
      formattedVal = (val / 1e9).toFixed(1) + "B";
    } else if (absVal >= 1e6) {
      formattedVal = (val / 1e6).toFixed(1) + "M";
    } else if (absVal >= 1e3) {
      formattedVal = (val / 1e3).toFixed(1) + "K";
    } else {
      val = absVal.toFixed(2);
      formattedVal = val.toString();
    }
  }
  return formattedVal;
}

export function getFormattedValWithSign(val: any) {
  if (val == null || isNaN(val)) return "0";

  const absVal = Math.abs(Number(val));
  let numberPart: number;
  let suffix = "";

  if (absVal >= 1e9) {
    numberPart = absVal / 1e9;
    suffix = "B";
  } else if (absVal >= 1e6) {
    numberPart = absVal / 1e6;
    suffix = "M";
  } else if (absVal >= 1e3) {
    numberPart = absVal / 1e3;
    suffix = "K";
  } else {
    numberPart = absVal;
  }

  let formattedNumber = Formatter.formatNumber(numberPart, true);

  if (suffix) {
    formattedNumber = formattedNumber.slice(0, -1);
    if (formattedNumber.endsWith(".0")) {
      formattedNumber = formattedNumber.slice(0, -2);
    }
  }

  let formattedVal = `${formattedNumber}${suffix}`;

  if (val < 0) {
    formattedVal = "-" + formattedVal;
  }

  return formattedVal;
}

export function handleDataPointsOnTrendGraphs(this: any, isEnabled = false) {
  this.customerData.forEach((dataItem: any) => {
    initializeMetrics.bind(this)();
    const { gdpName, gdpValue } = processActualValues.bind(this)(
      dataItem.value.actual_value
    );
    const lineTrendGrph = this.getLineTrendGraph(
      dataItem.value,
      gdpName,
      gdpValue,
      isEnabled
    );
    dataItem.trend = lineTrendGrph;
  });
  this.financeData.forEach((dataItem: any) => {
    initializeMetrics.bind(this)();
    const { gdpName, gdpValue } = processActualValues.bind(this)(
      dataItem.value.actual_value
    );
    const lineTrendGrph = this.getLineTrendGraph(
      dataItem.value,
      gdpName,
      gdpValue,
      isEnabled
    );
    dataItem.trend = lineTrendGrph;
  });
  this.engineerData.forEach((dataItem: any) => {
    initializeMetrics.bind(this)();
    const { gdpName, gdpValue } = processActualValues.bind(this)(
      dataItem.value.actual_value
    );
    const lineTrendGrph = this.getLineTrendGraph(
      dataItem.value,
      gdpName,
      gdpValue,
      isEnabled
    );
    dataItem.trend = lineTrendGrph;
  });
  this.peopleData.forEach((dataItem: any) => {
    initializeMetrics.bind(this)();
    const { gdpName, gdpValue } = processActualValues.bind(this)(
      dataItem.value.actual_value
    );
    const lineTrendGrph = this.getLineTrendGraph(
      dataItem.value,
      gdpName,
      gdpValue,
      isEnabled
    );
    dataItem.trend = lineTrendGrph;
  });
}
